import { toastDefault, toastError } from "../components/base/Toast";
import Color from 'color';
import Job from "../modules/crm/jobs/models/Job";
import { PaymentTypes } from "./Enums";



// Rates (technician share, source share, card/check fees, ...) are percentages,
// so a valid value is always a number between 0 and 100 — both ends included.
// Decimals are allowed, and a comma is accepted as the decimal separator.
export const isValidPercent = (value: any): boolean => {
    if (value === undefined || value === null || value.toString().trim() === "") return false;
    const num = Number(value.toString().replace(/,/g, "."));
    return !isNaN(num) && num >= 0 && num <= 100;
}

export type PasswordStrengthLevel = "empty" | "weak" | "fair" | "good" | "strong"

export interface PasswordStrengthResult {
    /** 0-5, one point for each rule the password meets. */
    score: number
    /** 0-4, how many of the meter bars are filled. */
    bars: number
    level: PasswordStrengthLevel
    /** Colour for the filled bars and for the hint under them. */
    color: "" | "danger" | "warning" | "success"
    /** A single bar is refused as too weak; two bars or more may be used. */
    acceptable: boolean
    message: string
}

// Password strength for the sign-up meter, mirrored by PasswordStrength() on the backend so the
// client and the server never disagree about what is acceptable. One point for each rule met —
// 8 or more characters, a lowercase letter, an uppercase letter, a digit, a symbol — drawn as
// four bars: red on one bar (refused), amber on two, green on three or four.
export const getPasswordStrength = (value?: string): PasswordStrengthResult => {

    const password = value ?? ""

    if (password.length === 0) {
        return {
            score: 0, bars: 0, level: "empty", color: "", acceptable: false,
            message: "Use 8 or more characters with a mix of letters, numbers & symbols."
        }
    }

    let score = 0
    if (password.length >= 8) score++
    if (/[a-z]/.test(password)) score++
    if (/[A-Z]/.test(password)) score++
    if (/[0-9]/.test(password)) score++
    if (/[^a-zA-Z0-9]/.test(password)) score++

    // Four bars for five rules: the first bar needs two rules, then one rule fills each bar.
    const bars = Math.max(0, score - 1)

    // Length is a hard floor, not just one rule among five: "Aa1!" meets four of the rules but
    // is four characters long, and the backend refuses anything shorter than eight. Cap it at a
    // single red bar so a short password never reads as acceptable.
    if (password.length < 8) return {
        score, bars: Math.min(bars, 1), level: "weak", color: "danger", acceptable: false,
        message: "Too short — use 8 or more characters with a mix of letters, numbers & symbols."
    }

    if (bars <= 1) return {
        score, bars, level: "weak", color: "danger", acceptable: false,
        message: "Too weak — use 8 or more characters with a mix of letters, numbers & symbols."
    }

    if (bars === 2) return {
        score, bars, level: "fair", color: "warning", acceptable: true,
        message: "Fair — this will do, but adding upper case, numbers or symbols is safer."
    }

    if (bars === 3) return {
        score, bars, level: "good", color: "success", acceptable: true,
        message: "Good password."
    }

    return {
        score, bars, level: "strong", color: "success", acceptable: true,
        message: "Strong password."
    }
}

export const getRandomNumber = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min
}

export const getRandomString = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

export const copyToClipboard = async (text?: string, message?: string): Promise<boolean> => {
    try {
        if (!text) return false;
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            await navigator.clipboard.writeText(text)
            if (message) toastDefault(message)
            return true;
        } else {
            const textArea = document.createElement('textarea')
            textArea.value = text
            textArea.style.position = 'fixed'
            textArea.style.opacity = '0'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()

            const successful = document.execCommand('copy')
            document.body.removeChild(textArea)

            if (successful) {
                if (message) toastDefault(message)
                return true;
            }
            else return false;
        }
    } catch (err) {
        //toastError("Can't copy the job because it's not supported in your browser!")
        return false;
    }
}

export const convertTo12HourFormat = (time24?: string, hidePeriod?: boolean): string => {
    if (!time24) return "";
    const [hourStr, minute] = time24.split(":");
    const hour = parseInt(hourStr, 10);
    const period = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    if (hidePeriod) return `${hour12}:${minute}`;
    return `${hour12}:${minute} ${period}`;
}



/** Same colour at a given alpha, e.g. withAlpha("#3B82F6", .12) -> "rgba(59, 130, 246, 0.12)". */
export const withAlpha = (color?: string, a: number = 1) => {
    if (!color) return "transparent";
    try {
        return Color(color).alpha(a).rgb().string();
    } catch {
        return "transparent";
    }
}


export const getJobStatusLightenColor = (job?: Job) => {
    if (!job) return undefined;
    if (!job.jobStatus?.color) return undefined;

    var color = Color(job.jobStatus?.color);

    return color.lighten(.9).hex();
}



export function isMobileDevice() {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;

    // 1) کرومیوم‌های جدید
    // @ts-ignore
    if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
        // @ts-ignore
        return navigator.userAgentData.mobile;
    }

    const ua = navigator.userAgent.toLowerCase();

    // 2) iPadOS 13+ خودش را macOS معرفی می‌کند؛ وجود تاچ نشانۀ آیپد است
    const iPadOS13Up = ua.includes('macintosh') && 'ontouchend' in document;

    // 3) الگوهای رایج موبایل/تبلت
    const mobileRe = /iphone|ipod|android.*mobile|windows phone|blackberry|bb10|mobile safari/;
    const tabletRe = /ipad|android(?!.*mobile)|tablet|kindle|silk/;

    // 4) نشانه‌های فیزیکی
    const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;
    const smallScreen = Math.min(window.screen.width, window.screen.height) <= 820;

    return (
        mobileRe.test(ua) ||
        tabletRe.test(ua) ||
        iPadOS13Up ||
        (coarsePointer && smallScreen)
    );
}



export function parseNumericEnum<E extends Record<string, string | number>>(
    e: E,
    s: string
): E[keyof E] | undefined {
    const n = Number(s);
    if (!Number.isFinite(n)) return undefined;
    const numericValues = Object.values(e).filter(v => typeof v === "number") as number[];
    return numericValues.includes(n) ? (n as E[keyof E]) : undefined;
}



export const getPaymentTypeDetails = (type?: PaymentTypes) => {
    var res: { name?: string, icon?: any, iconType?: any } = {}

    if (type == PaymentTypes.Cash) {
        res.name = "Cash";
        res.icon = "money-bill";
    }
    if (type == PaymentTypes.Check) {
        res.name = "Check";
        res.icon = "money-check-dollar";
    }
    if (type == PaymentTypes.CCCharge) {
        res.name = "CC Charge";
        res.icon = "credit-card";
    }
    if (type == PaymentTypes.CCManual) {
        res.name = "CC Manual";
        res.icon = "credit-card";
    }


    if (type == PaymentTypes.Rinvox) {
        res.name = "Rinvox";
        res.icon = "badge-dollar";
    }

    if (type == PaymentTypes.Wise) {
        res.name = "Wise";
        res.icon = "badge-dollar";
    }

    if (type == PaymentTypes.Venmo) {
        res.name = "Venmo";
        res.icon = "badge-dollar";
    }
    if (type == PaymentTypes.PayPal) {
        res.name = "PayPal";
        res.icon = "paypal";
        res.iconType = "brands";
    }
    if (type == PaymentTypes.Zelle) {
        res.name = "Zelle";
        res.icon = "badge-dollar";
    }
    if (type == PaymentTypes.CashApp) {
        res.name = "Cash App";
        res.icon = "cash-app";
        res.iconType = "brands";
    }
    if (type == PaymentTypes.ApplePay) {
        res.name = "Apple Pay";
        res.icon = "apple";
        res.iconType = "brands";
    }
    if (type == PaymentTypes.GooglePay) {
        res.name = "Google Pay";
        res.icon = "google";
        res.iconType = "brands";
    }
    return res;
}

// E-payments are every payment type after the Rinvox gateway (same rule as the backend's
// JobPaymentRates.IsEPay), so a type added to the enum later shows up wherever these are looped.
export const isEPayType = (type?: PaymentTypes) => (type ?? 0) > PaymentTypes.Rinvox;

export const getEPayTypes = (): PaymentTypes[] =>
    (Object.values(PaymentTypes).filter(v => typeof v === "number") as PaymentTypes[])
        .filter(isEPayType)
        .sort((a, b) => a - b);

export const getEPayRate = (rates: { paymentType?: PaymentTypes, rate?: number }[] | undefined, type: PaymentTypes) =>
    rates?.find(r => r.paymentType == type)?.rate;


// Straight-line distance in miles between two positions (the same figure the hub puts on
// "x mi away"): for distances worked out in the browser from a live position.
export const haversineMiles = (a: { lat: number, lng: number }, b: { lat: number, lng: number }): number => {
    const R = 3958.8;
    const toRad = (d: number) => d * Math.PI / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat), lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}
